import { useEffect, useState } from 'react'

type Listener<T> = (state: T) => void

export interface SharedStore<T> {
  /** Hook — subscribes the calling component to state changes. */
  use: () => T
  /** Imperative read of current state (outside a render). */
  get: () => T
  /** Imperative update — re-renders every subscribed component. */
  set: (next: T) => void
}

/**
 * Module-level state with React subscription. Used for global state that
 * outlives any single page (session, workspace cache).
 *
 *   const sessionStore = createSharedStore<Session>({ status: 'probing', user: null })
 *   function Page() { const s = sessionStore.use(); ... }
 *   // elsewhere:
 *   sessionStore.set({ status: 'signed-in', user: { ... } })
 */
export function createSharedStore<T>(initial: T): SharedStore<T> {
  let state = initial
  const listeners = new Set<Listener<T>>()
  return {
    use: () => {
      const [s, setS] = useState(state)
      useEffect(() => {
        listeners.add(setS)
        // Sync if state changed between mount and subscription.
        if (state !== s) setS(state)
        return () => { listeners.delete(setS) }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [])
      return s
    },
    get: () => state,
    set: (next) => {
      state = next
      listeners.forEach(l => l(next))
    },
  }
}
